import { mergeWith, omit, sumBy, without, isEqual, isObject, cloneDeep } from 'lodash'
import { differenceInCalendarDays, compareAsc, addMinutes, isAfter } from 'date-fns'
import { POST } from '@/api'
import { toHourClock, today } from '@/utils/date'
import { isOverlap, reserveFooterAlert, isResrakuReserve } from '@/utils/reserve'
import { normalizeTime } from '@/utils/time'
import { isGnaviSync, isResrakuAlignment } from '@/utils/store'
import { isWalkin, isBlock } from '@/resources/reservation/typeMap'
import typeRoomMap from '@/resources/reservation/typeRoomMap'
import typeSmokingMap from '@/resources/reservation/typeSmokingMap'
import typeTableMap from '@/resources/reservation/typeTableMap'
import detail from './detail'

const initialReserve = {
  reserveDatetime: null,
  resetTime: null,
  customer: {},
  tags: [],
  tables: [],
  tableIds: [],
  courses: [],
}

const initialPeopleChanged = { adults: false, children: false }

export default {
  namespaced: true,
  state: {
    reservation: initialReserve,
    busy: false,
    courseAlert: false,
    customerDetail: {},
    staffId: null,
    confirmed: false,
    showDetails: false,
    peopleChanged: initialPeopleChanged,
    initialCourses: [],
    initialTableId: null,
  },
  modules: {
    detail,
  },
  getters: {
    changed: (state, getters, rootState) => !isEqual(rootState.reserves.detail.reservation, state.reservation),
    /** 30時制に変換した reserveDatetime のオブジェクトを返します */
    reserveDatetime: ({ reservation }) => (reservation.reserveDatetime ? toHourClock(reservation.reserveDatetime) : {}),
    zone: (state, { reserveDatetime }, rootState, { 'stores/zone/typeByTime': zoneTypeByTime }) =>
      zoneTypeByTime ? zoneTypeByTime(reserveDatetime.time) : 'dinner',
    defaultUtilizationTime(_, { zone }, rootState) {
      return rootState.stores.current.store.time[`${zone}Utilization`]
    },
    reserve: ({ reservation }, { zone }, { stores }) => {
      const currentStore = stores.current.store
      return {
        ...reservation,
        utilizationTime: reservation.utilizationTime || currentStore.time[`${zone}Utilization`],
        resetTime: reservation.resetTime !== null ? reservation.resetTime : currentStore.time.reset,
      }
    },
    /**
     * 予約が重複しているかを返却します
     */
    isOverlap: (state, { reserve }, rootState, { 'reserves/all': allReserves }) =>
      reserve.tableIds.length > 0 && allReserves.some(b => isOverlap(reserve, b)),
    overCapacity({ reservation }, getters, { tables }) {
      const tablesById = (tables && tables.byId) || {}
      const { adults = 0, children = 0, tableIds } = reservation
      return Math.max(adults + children - sumBy(tableIds, tableId => (tablesById[tableId] || {}).maxAvailableSeat), 0)
    },
    hasCustomerName: ({ reservation: { customer } }) =>
      customer.lastNameKana || customer.firstNameKana || customer.lastName || customer.firstName,
    hasCustomerTel: ({ reservation: { customer } }) => customer.tel || customer.tel2,
    isWalkin: ({ reservation }) => isWalkin(reservation.type),

    /**
     * 選択された席と一致していない席指定条件を返します
     */
    unMatchedTypes: ({ reservation }, getters, { tables, syncGnavis }) => {
      if (!tables || !syncGnavis) return []
      const { tableIds, tableType, tableTypeLabel, privRoomType, smokingType } = reservation
      const isAlertableSmokingType = [1, 2].includes(smokingType)

      if (!tableIds.length || tableIds.includes(-1) || (!tableType && !privRoomType && !smokingType)) return []
      const unMatched = tableIds.reduce(
        (matched, id) => {
          const table = tables.byId[id] || {}
          const syncGnavi = syncGnavis.byId[table.syncGnaviId]
          if (!syncGnavi) {
            return {
              ...matched,
              smokingType:
                matched.smokingType ||
                (table.smokingType && table.smokingType !== smokingType && isAlertableSmokingType),
            }
          }
          return {
            tableType: matched.tableType || (!!tableType && syncGnavi.tableType !== tableType),
            privRoomType:
              matched.privRoomType ||
              (!!privRoomType && !!syncGnavi.privRoomType && syncGnavi.privRoomType !== privRoomType),
            smokingType:
              matched.smokingType ||
              (!!syncGnavi.smokingType && syncGnavi.smokingType !== smokingType && isAlertableSmokingType),
          }
        },
        { tableType: false, privRoomType: false, smokingType: false },
      )
      return [
        unMatched.tableType && (tableTypeLabel || typeTableMap[tableType]),
        unMatched.smokingType && typeSmokingMap[smokingType],
        unMatched.privRoomType && typeRoomMap[privRoomType],
      ].filter(o => o)
    },
    isOverDayReservedByGnavi(state, { reserveDatetime, 'detail/isReservedByGnavi': isReservedByGnavi }) {
      return (
        !!state.detail.reservation.id &&
        isReservedByGnavi &&
        isAfter(toHourClock(state.detail.reservation.reserveDatetime).date, today()) &&
        differenceInCalendarDays(reserveDatetime.date, today()) >= 90
      )
    },
    /**
     * ぐるなびネット予約の過去予約かどうか返します
     */
    disableEditDateReservedByGnavi(state, { 'detail/isReservedByGnavi': isReservedByGnavi }) {
      return isReservedByGnavi && compareAsc(new Date(), state.detail.reservation.reserveDatetime) >= 1
    },
    /**
     * ぐるなびネット予約の過去予約で予約日を7日経過したかどうか返します
     */
    disableEditTimeReservedByGnavi(state, { 'detail/isReservedByGnavi': isReservedByGnavi }) {
      return isReservedByGnavi && differenceInCalendarDays(new Date(), state.detail.reservation.reserveDatetime) >= 7
    },
    /**
     * ぐるなびネット予約の過去予約の「日付」を変更した場合か、
     * 予約日を7日経過した予約の「日時」を変更した場合にtrueを返します
     */
    disableEditDatetimeReservedByGnavi(state, getters) {
      const { reserveDatetime, disableEditDateReservedByGnavi, disableEditTimeReservedByGnavi } = getters
      const origin = state.detail.reservation
      const originReserveDatetime = toHourClock(origin.reserveDatetime)
      return (
        (disableEditDateReservedByGnavi && originReserveDatetime.date !== reserveDatetime.date) ||
        (disableEditTimeReservedByGnavi &&
          (!isEqual(originReserveDatetime, reserveDatetime) ||
            state.reservation.utilizationTime !== origin.utilizationTime))
      )
    },
    /**
     * 予約内容が営業時間内かどうかを返却します
     */
    isBusinessHours: (state, { reserve }, rootState) => {
      const { lunch, dinner, isBusinessDay } = rootState.stores.zone
      const businessHours = []
      const start = toHourClock(reserve.reserveDatetime).time.replace(':', '')
      const end = toHourClock(addMinutes(reserve.reserveDatetime, reserve.utilizationTime)).time.replace(':', '')

      const isContinuous = !(!dinner || !lunch || lunch.end !== dinner.start)
      if (lunch) {
        businessHours.push([lunch.start, isContinuous ? dinner.end : lunch.end])
      }
      if (dinner && !isContinuous) {
        businessHours.push([dinner.start, dinner.end])
      }
      return (
        isBusinessDay && businessHours.some(zone => normalizeTime(zone[0]) <= start && end <= normalizeTime(zone[1]))
      )
    },
    /**
     * 超過人数の値を返却します
     */
    deficiency(state, { reserve }, { tables }) {
      const { adults, children, tableIds } = reserve
      if (tableIds.includes(-1)) return 0
      return Math.max(adults + children - sumBy(tableIds, id => (tables.byId[id] || {}).maxAvailableSeat), 0)
    },
    /**
     * ブロック予約が6時間超過しているかどうかを返却します
     */
    isBlockOverTime(state, { reserve }, rootState) {
      return isBlock(reserve.type) && isGnaviSync(rootState.stores.current.store) && reserve.utilizationTime > 360
    },
    /**
     * 確認吹き出しアラート文言を返却します
     */
    alertMessages(state, { isBlockOverTime, isOverlap: _isOverlap, deficiency, isBusinessHours, unMatchedTypes }) {
      return [
        _isOverlap && '予約が重複',
        isBlockOverTime &&
          'ぐるなびネット予約のブロックは最長6時間になります（6時間を超える場合は分けて登録してください）',
        deficiency && '選択した席に対し予約人数がオーバー',
        !isBusinessHours && '定休日または営業時間外が選択されています',
        unMatchedTypes.length && `[ ${reserveFooterAlert(unMatchedTypes)} ] 指定のご予約です`,
      ].filter(Boolean)
    },
    /**
     * ぐるなび連携予約の場合、999人を超える人数変更は不可
     */
    isNumberExceeded(state, { reserve, 'detail/isReservedByGnavi': isReservedByGnavi }) {
      return isReservedByGnavi && reserve.adults + reserve.children >= 999
    },
  },
  mutations: {
    set(state, reservation) {
      state.reservation = { ...initialReserve, ...reservation }
    },
    setStaffId(state, staffId) {
      state.staffId = staffId
    },
    setShowDetails(state, showDetails) {
      state.showDetails = showDetails
    },
    setPeopleChanged(state, peopleChanged) {
      state.peopleChanged = peopleChanged
    },
    setCustomerDetail(state, customerDetail) {
      state.customerDetail = customerDetail
    },
    update(state, reservation) {
      state.reservation = mergeWith({}, state.reservation, reservation, (o, s) => (Array.isArray(o) ? s : undefined))
      // クーポン付き予約、幹事ポイント対象予約は人数とコース数を一致させる
      if (
        (state.reservation.registerPointType === '01' || state.reservation.coupon?.text) &&
        state.reservation.courses.length &&
        (Object.keys(reservation)[0] === 'adults' || Object.keys(reservation)[0] === 'children')
      ) {
        const { adults = 0, children = 0 } = state.reservation
        state.reservation.courses[0].count = adults + children
      }
    },
    setInitialCourses(state, courses) {
      state.initialCourses = courses
    },
    updateCourse(state, { index, course }) {
      const courses = state.reservation.courses.length ? state.reservation.courses : [{}]
      courses[index] = { ...courses[index], ...course }
      state.reservation = mergeWith({}, state.reservation, { courses }, (o, s) => (Array.isArray(o) ? s : undefined))
    },
    reset(state) {
      state.customerDetail = {}
      state.confirmed = false
      state.staffId = null
      state.peopleChanged = initialPeopleChanged
      state.initialCourses = []
      state.reservation = { ...state.detail.reservation }
      if (isBlock(state.reservation.type)) state.initialTableId = state.initialTableId || state.reservation.tableIds[0]
    },
    resetTable(state) {
      const { tableIds } = state.reservation
      state.reservation.tableIds = tableIds.includes(-1) ? [-1] : [...state.detail.reservation.tableIds]
    },
    toggleTable(state, id) {
      if (Array.isArray(id)) {
        state.reservation.tableIds = id
      } else {
        const { tableIds } = state.reservation
        const toggle = tableIds.includes(id) ? without(tableIds, id) : without(tableIds, -1).concat(id)
        state.reservation.tableIds = id === -1 ? [-1] : toggle
      }
    },
    confirm(state, staffId) {
      state.staffId = staffId
      state.confirmed = true
    },
    busy(state, value) {
      state.busy = !!value
    },
    courseAlert(state, check) {
      state.courseAlert = check
    },
  },
  actions: {
    async start({ state, commit, dispatch }, reserve) {
      const id = isObject(reserve) ? reserve.id : reserve
      if (id) {
        commit('setShowDetails', true)
        if (state.detail.reservation.id !== id) await dispatch('detail/get', { id })
      } else {
        commit('setShowDetails', false)
        commit('detail/set', reserve || {})
      }
      // TODO 参照がdetailなのでupdateCourseで人数が変わるとeditとdetailが同時に変わってしまう
      // cloneDeep(state.detail.reservation)としたい
      commit('set', state.detail.reservation)
      commit('setInitialCourses', cloneDeep(state.detail.reservation.courses))
      if (isBlock(state.reservation.type)) state.initialTableId = state.reservation.tableIds[0]
    },
    async attachCustomer({ commit, dispatch }, id) {
      const customer = await dispatch('customers/detail/get', { id }, { root: true })
      if (customer) {
        commit('update', {
          customer: {
            id: customer.id,
            firstName: customer.name.first,
            firstNameKana: customer.name.firstKana,
            lastName: customer.name.last,
            lastNameKana: customer.name.lastKana,
            tel: customer.tel1,
            tel1DuplicationFlag: customer.tel1DuplicationFlag,
            tel2: customer.tel2,
            tel2DuplicationFlag: customer.tel2DuplicationFlag,
            officeName: customer.company.name,
            mail1: customer.mail1,
            mail2: customer.mail2,
          },
        })
        commit('setCustomerDetail', customer)
      }
    },
    async confirm({ commit, getters, rootState, state }, staffId) {
      if (state.busy) return false
      commit('busy', true)

      const currentStore = rootState.stores.current.store
      const { customer, reserveDatetime, tableIds, courses, tags, syncType, ...reservation } = getters.reserve

      const { status, message } = await POST('/api/web/reservation/regist/confirm', {
        storeId: currentStore.id,
        customer: omit(customer, [
          'tel2DuplicationFlag',
          'tel1DuplicationFlag',
          'visitCount',
          'attributes',
          'syncMemberType',
        ]),
        reservation: {
          ...omit(reservation, [
            'receiptDatetime',
            'settlementPoint',
            'staffName',
            'status',
            'tables',
            'privRoomType',
            'smokingType',
            'tableType',
            'syncRsvId',
          ]),
          reserveDate: omit(toHourClock(reserveDatetime), ['date', 'time']),
          tags: tags ? tags.map(tag => tag.id).join(',') : '',
          table: tableIds.join(','),
          courses: courses.filter(c => c.id),
        },
        staffId,
        duplicatedIds: '', // TODO 必要？
      })

      if (status === 200) {
        commit('confirm', staffId)
      } else {
        commit('app/error', { status, message }, { root: true })
      }

      commit('busy', false)
      return status === 200
    },
    async save(store, staffId) {
      const { state, getters, commit, rootState } = store
      if (state.busy) return false
      commit('busy', true)

      const currentStore = rootState.stores.current.store
      const { customer, reserveDatetime, tableIds, courses, tags, syncType, ...reservation } = getters.reserve
      const isResraku = isResrakuReserve(getters.reserve)
      const collaboration = { syncType: syncType || undefined }
      if ((isResraku.hotpepper || isResraku.inquiry) && !isResrakuAlignment(currentStore)) {
        collaboration.status = 4
      }

      const { status, message, data } = await POST('/api/web/reservation/regist/execute', {
        storeId: currentStore.id,
        customer: omit(customer, ['tel2DuplicationFlag', 'tel1DuplicationFlag', 'visitCount', 'attributes']),
        reservation: {
          ...omit(reservation, [
            'receiptDatetime',
            'settlementPoint',
            'staffName',
            'status',
            'tables',
            'privRoomType',
            'smokingType',
            'tableType',
            'syncRsvId',
          ]),
          reserveDate: omit(toHourClock(reserveDatetime), ['date', 'time']),
          tags: tags ? tags.map(tag => tag.id).join(',') : '',
          table: tableIds.join(','),
          courses: courses.filter(c => c.id),
          collaboration,
        },
        staffId: staffId || state.staffId,
      })

      const { reservationId, customerId } = data
      if (status === 200) {
        commit('detail/set', {
          ...getters.reserve,
          id: reservationId,
          customer: {
            ...getters.reserve.customer,
            id: customerId,
          },
        })
      } else {
        commit('app/error', { status, message }, { root: true })
      }
      commit('busy', false)
      return status === 200 ? reservationId : null
    },
  },
}
